import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, apiClient } from '../../lib/api-client';

interface OAuthButtonsProps {
  action?: 'login' | 'register';
}

export const OAuthButtons: React.FC<OAuthButtonsProps> = ({ action = 'login' }) => {
  const { t } = useTranslation();
  const [googleConfigured, setGoogleConfigured] = useState<boolean>(false);

  useEffect(() => {
    let mounted = true;
    apiClient
      .get<{ google: boolean }>('/auth/providers')
      .then((res) => {
        if (mounted) setGoogleConfigured(res.data.google);
      })
      .catch(() => {
        if (mounted) setGoogleConfigured(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const googleLabel =
    action === 'login' ? t('auth.oauth.googleLogin') : t('auth.oauth.googleRegister');
  const ariaLabel = googleConfigured
    ? googleLabel
    : t('auth.oauth.comingSoonAria', { action: googleLabel, provider: 'Google' });

  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE_URL}/auth/google`;
  };

  return (
    <div className="space-y-3">
      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-primary/10"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-white px-4 text-slate-500">{t('auth.oauth.or')}</span>
        </div>
      </div>

      {/* Google Button */}
      <button
        type="button"
        disabled={!googleConfigured}
        onClick={googleConfigured ? handleGoogleLogin : undefined}
        className={
          googleConfigured
            ? 'w-full rounded-2xl border border-primary/20 bg-white px-4 py-3 text-slate-700 transition-colors flex items-center justify-center gap-3 hover:bg-slate-50'
            : 'w-full cursor-not-allowed rounded-2xl border border-primary/10 bg-slate-50 px-4 py-3 text-slate-400 transition-colors flex items-center justify-center gap-3'
        }
        aria-label={ariaLabel}
      >
        {/* Google Icon */}
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        <span>{googleLabel}</span>
        {!googleConfigured && (
          <span className="ml-auto rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-500">
            {t('auth.oauth.comingSoon')}
          </span>
        )}
      </button>
    </div>
  );
};

export default OAuthButtons;
